import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { color, space, font, radius, shadow, zIndex } from './tokens';

// ---------------------------------------------------------------------------
// Toast — Wave 4.1.A
//
// Global notification system. Wrap the app once in <ToastProvider>, then
// any descendant calls `useToast()` to emit success/error/info/warning
// notifications.
//
//   const { notify } = useToast();
//   notify({ variant: 'success', message: 'Endpoint created' });
//
// Toasts auto-dismiss after `duration` (default 4s) and stack in the
// bottom-right corner.
// ---------------------------------------------------------------------------

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastInput {
  variant?:  ToastVariant;
  title?:    string;
  message:   string;
  duration?: number;
}

interface ToastRecord extends ToastInput {
  id: string;
}

interface ToastContextValue {
  notify: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be called inside <ToastProvider>');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((input: ToastInput): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((current) => [...current, { ...input, id }]);
    return id;
  }, []);

  return (
    <ToastContext.Provider value={{ notify, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
};

const ToastViewport: React.FC<{ toasts: ToastRecord[]; dismiss: (id: string) => void }> = ({
  toasts,
  dismiss,
}) => {
  if (toasts.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position:     'fixed',
        bottom:       space[6],
        right:        space[6],
        display:      'flex',
        flexDirection:'column',
        gap:          space[2],
        zIndex:       zIndex.toast,
        maxWidth:     400,
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} record={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
};

const ToastItem: React.FC<{ record: ToastRecord; onDismiss: () => void }> = ({
  record,
  onDismiss,
}) => {
  const variant = record.variant ?? 'info';
  const duration = record.duration ?? 4000;
  const c = color.status[variant];

  useEffect(() => {
    if (duration <= 0) return;
    const t = setTimeout(onDismiss, duration);
    return () => clearTimeout(t);
  }, [duration, onDismiss]);

  return (
    <div
      role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
      aria-live={variant === 'error' || variant === 'warning' ? 'assertive' : 'polite'}
      style={{
        background:   color.bg.card,
        border:       `1px solid ${c.border}`,
        borderLeft:   `4px solid ${c.text}`,
        borderRadius: radius.md,
        boxShadow:    shadow.lg,
        padding:      `${space[3]}px ${space[4]}px`,
        display:      'flex',
        alignItems:   'flex-start',
        gap:          space[3],
        animation:    'ds-toast-in 200ms cubic-bezier(0,0,0.2,1)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {record.title && (
          <div style={{
            fontSize:   font.size.base,
            fontWeight: font.weight.semibold,
            color:      color.text.primary,
            marginBottom: 2,
          }}>
            {record.title}
          </div>
        )}
        <div style={{
          fontSize: font.size.sm,
          color:    color.text.secondary,
          lineHeight: font.lineHeight.snug,
          wordWrap: 'break-word',
        }}>
          {record.message}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        style={{
          background: 'transparent',
          border:     'none',
          color:      color.text.muted,
          cursor:     'pointer',
          fontSize:   font.size.lg,
          lineHeight: 1,
          padding:    0,
        }}
      >
        ×
      </button>
    </div>
  );
};

export default ToastProvider;
